import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import type {
  ApprovalNote,
  Approval,
  Bill,
  BillingSummary,
  CashDrawer,
  CloseBillInput,
  FeeService,
  FeeSettings,
  PaymentInput,
  Receipt,
} from './types';

const LIVE_MS = 10_000;

export function useBills(status: string) {
  const { selected, canSelectAll } = useBranch();
  const branch = selected?.id ?? 'all';
  return useQuery({
    queryKey: ['bills', branch, status],
    queryFn: async ({ signal }) =>
      (
        await api.get<Bill[]>('/bills', {
          signal,
          query:
            status === 'today' ? { day: 'today', status: 'all' } : { status },
        })
      ).data,
    enabled: !!selected || canSelectAll,
    refetchInterval: LIVE_MS,
  });
}

/**
 * RL-1049: one patient's bills (any status) at the selected branch, for the
 * patient record's Billing tab. `GET /bills` is branch-scoped, so nothing is
 * fetched while "All" branches is selected.
 */
export function usePatientBills(patientId: string) {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: ['bills', branch, 'patient', patientId],
    queryFn: async ({ signal }) =>
      (
        await api.get<Bill[]>('/bills', {
          signal,
          query: { patientId, status: 'all' },
        })
      ).data,
    enabled: branch !== 'all',
  });
}

export function useBillingSummary() {
  const { selected, canSelectAll } = useBranch();
  const branch = selected?.id ?? 'all';
  return useQuery({
    queryKey: ['bills-summary', branch],
    queryFn: async ({ signal }) =>
      (await api.get<BillingSummary>('/bills/summary', { signal })).data,
    enabled: !!selected || canSelectAll,
    refetchInterval: LIVE_MS,
  });
}

export function useBill(id: string | null) {
  return useQuery({
    queryKey: ['bill', id],
    queryFn: async ({ signal }) =>
      (await api.get<Bill>(`/bills/${id}`, { signal })).data,
    enabled: !!id,
    // "The bill updates by itself when the bank confirms" (DuitNow QR).
    refetchInterval: (q) =>
      q.state.data?.duitNow && q.state.data.status === 'open' ? 3000 : false,
  });
}

export function useVoidRequests() {
  return useQuery({
    queryKey: ['bills', 'void-requests'],
    queryFn: async ({ signal }) =>
      (await api.get<Bill[]>('/bills/void-requests', { signal })).data,
    refetchInterval: LIVE_MS,
  });
}

export function useBillActions(id: string) {
  const qc = useQueryClient();
  const onSuccess = (b: Bill) => {
    qc.setQueryData(['bill', id], b);
    void qc.invalidateQueries({ queryKey: ['bills'] });
    void qc.invalidateQueries({ queryKey: ['bills-summary'] });
    void qc.invalidateQueries({ queryKey: ['limit-billing', 'bill', id] });
  };
  const post = (path: string, body?: unknown) =>
    api.post<Bill>(`/bills/${id}${path}`, body).then((r) => r.data);
  return {
    addLine: useMutation({
      mutationFn: (v: {
        kind: 'service' | 'other';
        serviceId?: string;
        labTestId?: string;
        description?: string;
        quantity: number;
        unitPriceSen?: number;
        covered?: boolean;
      }) => post('/lines', v),
      onSuccess,
    }),
    updateLine: useMutation({
      mutationFn: (v: {
        lineId: string;
        quantity?: number;
        unitPriceSen?: number;
        covered?: boolean;
        /** RL-1743: required to change a price the line already has. */
        reason?: string;
        /** The bill version shown: a stale one gets 409 VERSION_CONFLICT. */
        version?: number;
      }) =>
        api
          .patch<Bill>(`/bills/${id}/lines/${v.lineId}`, {
            quantity: v.quantity,
            unitPriceSen: v.unitPriceSen,
            covered: v.covered,
            reason: v.reason,
            version: v.version,
          })
          .then((r) => r.data),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: (v: {
        version: number;
        payerId?: string | null;
        memberPrice?: boolean;
      }) => api.patch<Bill>(`/bills/${id}`, v).then((r) => r.data),
      onSuccess,
    }),
    applyDeposit: useMutation({
      mutationFn: (input?: {
        limitAssessment?: PaymentInput['limitAssessment'];
      }) => post('/deposit', input),
      onSuccess,
    }),
    duitNow: useMutation({
      mutationFn: (input: {
        amountSen?: number;
        limitAssessment?: PaymentInput['limitAssessment'];
      }) => post('/duitnow', input),
      onSuccess,
    }),
    confirmDuitNow: useMutation({
      mutationFn: ({
        requestId,
        limitAssessment,
      }: {
        requestId: string;
        limitAssessment?: PaymentInput['limitAssessment'];
      }) =>
        post(`/duitnow/${requestId}/simulate-confirmation`, {
          limitAssessment,
        }),
      onSuccess,
    }),
    requestDiscount: useMutation({
      mutationFn: (v: { amountSen: number; reason: string; note?: string }) =>
        post('/discount-request', v),
      onSuccess,
    }),
    requestRefund: useMutation({
      mutationFn: (v: {
        amountSen: number;
        reason: string;
        refundBy: 'original' | 'cash';
        note?: string;
      }) => post('/refund-request', v),
      onSuccess,
    }),
    removeLine: useMutation({
      mutationFn: (lineId: string) => post(`/lines/${lineId}/remove`),
      onSuccess,
    }),
    pay: useMutation({
      mutationFn: (v: PaymentInput) => post('/payments', v),
      onSuccess,
    }),
    close: useMutation({
      mutationFn: (input: CloseBillInput) => post('/close', input),
      onSuccess,
    }),
    requestVoid: useMutation({
      mutationFn: (reason: string) => post('/void-request', { reason }),
      onSuccess,
    }),
    decideVoid: useMutation({
      mutationFn: (v: { approve: boolean; note?: string }) =>
        post('/void-decision', v),
      onSuccess,
    }),
  };
}

export function useBillDeposit(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['bill', id, 'deposit'],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ appliedSen: number; availableSen: number }>(
          `/bills/${id}/deposit`,
          { signal },
        )
      ).data,
    enabled,
  });
}

export function useReceipt(id: string | null) {
  return useQuery({
    queryKey: ['receipt', id],
    queryFn: async ({ signal }) =>
      (await api.get<Receipt>(`/bills/${id}/receipt`, { signal })).data,
    enabled: !!id,
  });
}

export function useCreateBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      patientId: string;
      payerId: string | null;
      serviceId: string;
      memberPrice: boolean;
    }) => api.post<Bill>('/bills', v).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bills'] });
      void qc.invalidateQueries({ queryKey: ['bills-summary'] });
    },
  });
}

// ------------------------------------------------------------ approvals

export function useApprovals(state: 'waiting' | 'decided') {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: ['approvals', branch, state],
    queryFn: async ({ signal }) =>
      (await api.get<Approval[]>('/approvals', { signal, query: { state } }))
        .data,
    refetchInterval: LIVE_MS,
  });
}

export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      id: string;
      kind: string;
      approve: boolean;
      note?: string;
    }) =>
      v.kind === 'void'
        ? api.post(`/bills/${v.id}/void-decision`, {
            approve: v.approve,
            note: v.note,
          })
        : api.post(`/approvals/${v.id}/decision`, {
            approve: v.approve,
            note: v.note,
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['bills'] });
      void qc.invalidateQueries({ queryKey: ['bill'] });
      void qc.invalidateQueries({ queryKey: ['cash-drawer'] });
    },
  });
}

// ------------------------------------------------------------ cash drawer

/** RL-1815: POST /approvals/:id/notes (for a void, `id` is the bill id). */
export function useAddApprovalNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; text: string }) =>
      api
        .post<ApprovalNote>(`/approvals/${v.id}/notes`, { text: v.text })
        .then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

export function useCashDrawer(enabled = true) {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: ['cash-drawer', branch],
    queryFn: async ({ signal }) =>
      (await api.get<CashDrawer>('/cash-drawer', { signal })).data,
    enabled: enabled && branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

export function useCheckers(enabled: boolean) {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: ['cash-drawer', 'checkers', branch],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ id: string; fullName: string }[]>(
          '/cash-drawer/checkers',
          { signal },
        )
      ).data,
    enabled: enabled && branch !== 'all',
  });
}

export function useCashDrawerActions() {
  const qc = useQueryClient();
  const onSuccess = () => {
    void qc.invalidateQueries({ queryKey: ['cash-drawer'] });
    void qc.invalidateQueries({ queryKey: ['approvals'] });
  };
  return {
    open: useMutation({
      mutationFn: (floatSen: number) =>
        api.post('/cash-drawer/shift', { floatSen }),
      onSuccess,
    }),
    paidOut: useMutation({
      mutationFn: (v: {
        amountSen: number;
        reason: string;
        receiptNumber?: string;
      }) => api.post('/cash-drawer/paid-outs', v),
      onSuccess,
    }),
    close: useMutation({
      mutationFn: (v: {
        notes: Record<string, number>;
        coinsSen: number;
        checkedById: string;
        differenceReason?: string;
      }) => api.post('/cash-drawer/shift/close', v),
      onSuccess,
    }),
  };
}

// ------------------------------------------------------------ fee schedule

export function useFeeServices(all = false) {
  return useQuery({
    queryKey: ['fee-services', all],
    queryFn: async ({ signal }) =>
      (
        await api.get<FeeService[]>('/fee-schedule/services', {
          signal,
          query: all ? { all: 'true' } : undefined,
        })
      ).data,
  });
}

export function useFeeSettings() {
  return useQuery({
    queryKey: ['fee-settings'],
    queryFn: async ({ signal }) =>
      (await api.get<FeeSettings>('/fee-schedule/settings', { signal })).data,
  });
}

export function useFeeScheduleActions() {
  const qc = useQueryClient();
  const onSuccess = () => {
    void qc.invalidateQueries({ queryKey: ['fee-services'] });
    void qc.invalidateQueries({ queryKey: ['fee-settings'] });
  };
  return {
    create: useMutation({
      mutationFn: (v: {
        name: string;
        durationMinutes?: number | null;
        priceSen?: number | null;
        memberPriceSen?: number | null;
        branchIds?: string[];
      }) =>
        api.post<FeeService>('/fee-schedule/services', v).then((r) => r.data),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: (v: {
        id: string;
        name?: string;
        durationMinutes?: number | null;
        priceSen?: number | null;
        memberPriceSen?: number | null;
        branchIds?: string[];
        isActive?: boolean;
      }) => {
        const { id, ...body } = v;
        return api
          .patch<FeeService>(`/fee-schedule/services/${id}`, body)
          .then((r) => r.data);
      },
      onSuccess,
    }),
    saveSettings: useMutation({
      mutationFn: (v: {
        serviceTaxEnabled?: boolean;
        serviceTaxRateBps?: number | null;
        serviceTaxThresholdSen?: number | null;
        serviceTaxScopeConfirmed?: boolean;
        sstRegistrationNo?: string | null;
        minGpFeeSen?: number | null;
        maxGpFeeSen?: number | null;
      }) =>
        api.put<FeeSettings>('/fee-schedule/settings', v).then((r) => r.data),
      onSuccess,
    }),
  };
}
